import { fetchBackgroundImages } from 'api/home/static';
import { IBackgroundImages } from 'api/home/types';
import { IRegionShort } from 'api/region/types';
import { IResortPathInfo } from 'api/resort/types';
import classNames from 'classnames';
import { BlockContent } from 'components/blocks/BlockContent';
import { BlockHeader } from 'components/blocks/BlockHeader';
import { BottomMountains } from 'components/blocks/BottomMountains';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import { NextLink } from 'components/blocks/NextLink';
import { HeaderNav } from 'components/common/HeaderNav';
import { Layout } from 'components/resort/Layout/Layout';
import { AdUnitContext } from 'googlead/components/AdUnitContext';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { HeadMeta } from 'components/common/HeadMeta';
import { Footer } from 'components/blocks/Footer';
import React, { useMemo } from 'react';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { locale } from 'i18n/constants';
import { Routes } from 'routes';
import { AnalyticsContext } from 'util/analytics';
import { fetchResortSlugs } from 'util/fetchResortSlugs';
import { fetchRegionSlugs } from 'util/fetchStaticPageRegion';

import { GetStaticProps, GetStaticPropsResult } from 'next';
import { HeaderNavCurLink } from 'components/common/HeaderNav/MobileSummerHeader';
import styles from './sitemap.module.scss';

export type Props = {
  resorts: Array<IResortPathInfo>,
  regions: Array<IRegionShort>,
  backgroundImage: IBackgroundImages;
};

export const DEBUG_TAG = 'sitemap-page';
export const TEMPLATE_ID = '9.10';
export const TEMPLATE_NAME = 'Sitemap';

export const NO_REGION = 'NO_REGION';

export const RegionLink = ({ region }: { region: IRegionShort }) => (
  <h4 className={styles.item}>
    <NextLink
      href={Routes.regionSnowReport(region)}
    >
      {region.title}
    </NextLink>
  </h4>
);
export const RegionWithResorts = ({ region, resorts }: { region: IRegionShort, resorts: Array<IResortPathInfo> }) => (
  <BoxWithShadow className={styles.shadowBox}>
    <BlockHeader
      title=""
      useH3
      headChildren={<NextLink href={Routes.regionSnowReport(region)}>{region.title}</NextLink>}
    />
    <BlockContent>
      <ul className={styles.resortList}>
        {resorts.sort((a, b) => a.title.localeCompare(b.title)).map((resort) => (
          <li>
            <NextLink href={Routes.resortSnowReport(resort)}>{resort.title}</NextLink>
          </li>
        ))}
      </ul>
    </BlockContent>
  </BoxWithShadow>
);

export default function SiteMapPage(
  {
    resorts, regions, backgroundImage,
  }: Props,
) {
  const allRegions = new Set(regions.map((r) => String(r.uuid)));
  const resortsInRegions: Record<string, Array<IResortPathInfo>> = useMemo(() => {
    const data: Record<string, Array<IResortPathInfo>> = {};
    resorts.forEach((r) => {
      if (allRegions.has(r.region_id)) {
        data[r.region_id] = data[r.region_id] || [];
        data[r.region_id].push(r);
      } else {
        data[NO_REGION] = data[NO_REGION] || [];
        data[NO_REGION].push(r);
      }
    });
    return data;
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { t } = useOTSTranslation('common');
  const block: Array<React.ReactNode> = [];
  let noResorts: Array<React.ReactNode> = [];
  regions.sort((a, b) => a.title.localeCompare(b.title)).forEach(((region) => {
    const resortList = (resortsInRegions[region.uuid] || []);
    if (resortList.length) {
      block.push(<RegionWithResorts key={region.uuid} region={region} resorts={resortList} />);
    } else {
      noResorts.push(<RegionLink key={region.uuid} region={region} />);
    }
  }));
  if (noResorts.length) {
    block.push(
      <BoxWithShadow className={styles.shadowBox}>
        <BlockHeader title={t('common.sitemapAdditionalRegions')} />
        <BlockContent>
          <div className={styles.noResorts}>
            {noResorts}
          </div>
        </BlockContent>
      </BoxWithShadow>,
    );
    noResorts = [];
  }
  if ((resortsInRegions[NO_REGION] || []).length) {
    block.push(
      <BoxWithShadow className={styles.shadowBox}>
        <BlockContent>
          <ul className={styles.resortList}>
            {(resortsInRegions[NO_REGION] || []).sort((a, b) => a.title.localeCompare(b.title)).map((resort) => (
              <li>
                <NextLink href={Routes.resortSnowReport(resort)}>{resort.title}</NextLink>
              </li>
            ))}
          </ul>
        </BlockContent>
      </BoxWithShadow>,
    );
  }

  return (
    <AnalyticsContext templateId={TEMPLATE_ID} templateName={TEMPLATE_NAME}>
      <AdUnitContext
        adType="other"
        adTKey="unsubscibe"
      >
        <div>
          <HeadMeta
            title={t('common.sitemapHeader')}
          />
          <HeaderNav current={HeaderNavCurLink.MAIN} />
          <header className={styles.header}>
            <div className={classNames(styles.fullBlurred)}>
              <div style={{ backgroundImage: `url(${backgroundImage.image})` }} />
            </div>
            <div className="hero gradient" />
            <div className="hero mountains" />
            <div className={classNames('container', styles.title)}>
              <h1>{t('common.sitemapHeader')}</h1>
            </div>
          </header>
          <div className="container">
            <Layout>
              <div className={styles.box}>
                {block}
              </div>
            </Layout>
          </div>
          <BottomMountains />
          <Footer />
        </div>
      </AdUnitContext>
    </AnalyticsContext>
  );
}

// This function gets called at build time
export const getStaticProps: GetStaticProps<Props> = async () => {
  const f: GetStaticPropsResult<Props> = {
    props: {
      backgroundImage: await fetchBackgroundImages(),
      resorts: await fetchResortSlugs('sitemap'),
      regions: await fetchRegionSlugs('sitemap'),
      ...(await serverSideTranslations(locale, ['common', 'unit', 'seo'])),
    },
  };
  return f;
};
